import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";

interface UseRunLikesReturn {
  likeCount: number;
  isLiked: boolean;
  isLoading: boolean;
  toggleLike: () => Promise<void>;
}

export function useRunLikes(runId: string | undefined): UseRunLikesReturn {
  const { user } = useAuth();
  const { toast } = useToast();
  const [likeCount, setLikeCount] = useState(0);
  const [isLiked, setIsLiked] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (runId) {
      fetchLikeData();
    }
  }, [runId, user?.id]);

  const fetchLikeData = async () => {
    if (!runId) return;

    try {
      // Get total like count
      const { count, error: countError } = await supabase
        .from('run_likes')
        .select('*', { count: 'exact', head: true })
        .eq('run_id', runId);

      if (countError) {
        console.error('Error fetching like count:', countError);
      } else {
        setLikeCount(count || 0);
      }

      // Check if current user has liked
      if (user?.id) {
        const { data: userLike, error: likeError } = await supabase
          .from('run_likes')
          .select('id')
          .eq('run_id', runId)
          .eq('user_id', user.id)
          .maybeSingle();

        if (likeError) {
          console.error('Error checking user like:', likeError);
        } else {
          setIsLiked(!!userLike);
        }
      }
    } catch (error) {
      console.error('Error fetching like data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleLike = useCallback(async () => {
    if (!runId || !user?.id) {
      toast({
        title: "Sign in required",
        description: "Please sign in to like adventures.",
        variant: "destructive",
      });
      return;
    }

    // Optimistic update
    const wasLiked = isLiked;
    setIsLiked(!wasLiked);
    setLikeCount(prev => wasLiked ? prev - 1 : prev + 1);

    try {
      if (wasLiked) {
        // Unlike
        const { error } = await supabase
          .from('run_likes')
          .delete()
          .eq('run_id', runId)
          .eq('user_id', user.id);

        if (error) throw error;
      } else {
        // Like
        const { error } = await supabase
          .from('run_likes')
          .insert({
            run_id: runId,
            user_id: user.id,
          });

        if (error) throw error;
      }
    } catch (error) {
      // Rollback on error
      setIsLiked(wasLiked);
      setLikeCount(prev => wasLiked ? prev + 1 : prev - 1);
      
      console.error('Error toggling like:', error);
      toast({
        title: "Error",
        description: "Failed to update like. Please try again.",
        variant: "destructive",
      });
    }
  }, [runId, user?.id, isLiked, toast]);

  return {
    likeCount,
    isLiked,
    isLoading,
    toggleLike,
  };
}
